import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@shared/ui/collapsible';
import { SuiteIcon } from '@shared/icons';
import { RecentPage } from '@shared/activity/types';
import { RecentActionNode } from './RecentActionNode';
import { relativeTime } from './relativeTime';
import { cn } from '@shared/lib/utils';

export const RecentPageNode: React.FC<{ page: RecentPage }> = ({ page }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCurrent = location.pathname === page.path;
  const hasActions = page.actions && page.actions.length > 0;
  const [isOpen, setIsOpen] = useState(isCurrent);
  const [showAll, setShowAll] = useState(false);

  const actions = showAll ? page.actions : page.actions.slice(0, 5);
  const remaining = page.actions.length - 5;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={cn('rounded text-xs my-0.5', isCurrent ? 'bg-accent/15' : 'hover:bg-muted/40')}
      role="treeitem"
      aria-expanded={isOpen}
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5 w-full">
        {hasActions ? (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-0.5 rounded text-muted-foreground hover:text-foreground shrink-0"
            aria-label={isOpen ? 'Collapse actions' : 'Expand actions'}
          >
            {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => navigate(page.path)}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          <SuiteIcon name={page.icon || page.suiteId} className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="truncate text-foreground font-medium" title={page.title}>{page.title}</span>
        </button>
        <span className="text-[10px] text-muted-foreground/60 shrink-0 font-mono">
          {relativeTime(page.lastVisitedAt)}
        </span>
      </div>

      {hasActions && (
        <CollapsibleContent className="pb-1">
          {actions.map((act) => (
            <RecentActionNode key={act.id} action={act} />
          ))}
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="text-[10px] text-muted-foreground hover:text-foreground pl-6 py-0.5 underline"
            >
              {showAll ? 'Show less' : `Show ${remaining} more...`}
            </button>
          )}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
};
